import { Typography } from "../../../../../../shared/frontend/ui";
import React from "react";
import type { ApiClient } from "../../app/api/client";
import { spacing } from "../../app/design-system";
import type { I18nContextValue } from "../../app/i18n/useI18n";
import { DocumentAttachmentPreviewCard } from "../../components/DocumentAttachmentPreviewCard";
import { EmailBodyView } from "../../components/EmailBodyView";

/** Werte von `bodyFormat` einer Sendung (kleingeschrieben verglichen). */
const BODY_FORMAT_DOCUMENT = "document";
const BODY_FORMAT_HTML = "html";

/**
 * Die Sendung so, wie sie beim Empfänger ankommt: der Text lesbar und der Anhang als Dokument.
 *
 * Dieselben Bausteine wie in der Personenakte (`PersonDigitalFileViewer`) — eine Nachricht sieht
 * überall gleich aus, egal von welcher Liste aus man sie öffnet.
 */

/** Wo der Anhang in der digitalen Akte liegt; der Server verknüpft ihn über den Objektschlüssel. */
type AttachmentLocation = {
  personId: string;
  documentId: string;
  fileName: string;
  contentType?: string | null;
};

type Props = {
  api: ApiClient;
  messageId: string;
  body?: string | null;
  bodyFormat?: string | null;
  attachmentFileName?: string | null;
  hasAttachment: boolean;
  text: I18nContextValue["text"];
};

export function OutboundMessageDocument({
  api,
  messageId,
  body,
  bodyFormat,
  attachmentFileName,
  hasAttachment,
  text,
}: Props) {
  const [attachment, setAttachment] = React.useState<AttachmentLocation | null>(null);

  // Beim Wechsel der Sendung direkt beim Rendern zuruecksetzen statt im Effect — sonst zeigte die
  // Ansicht einen Durchlauf lang den Anhang der vorigen Zeile.
  const [renderedMessageId, setRenderedMessageId] = React.useState(messageId);
  if (renderedMessageId !== messageId) {
    setRenderedMessageId(messageId);
    setAttachment(null);
  }

  React.useEffect(() => {
    if (!hasAttachment) return;
    let cancelled = false;
    api.get<AttachmentLocation>(`/api/v1/outbound-messages/${encodeURIComponent(messageId)}/attachment-location`)
      .then((location) => {
        if (!cancelled) setAttachment(location);
      })
      // Liegt der Anhang nicht in der Akte, bleibt es beim Dateinamen. Das ist kein Fehler des
      // Vorgangs, sondern eine Sendung ohne abgelegtes Dokument.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, hasAttachment, messageId]);

  const isDocumentOnly = String(bodyFormat || "").toLowerCase() === BODY_FORMAT_DOCUMENT;
  const hasBody = Boolean(String(body || "").trim()) && !isDocumentOnly;

  return (
    <div style={{ display: "grid", gap: spacing.sm }}>
      {hasBody ? (
        <EmailBodyView body={body} isHtml={String(bodyFormat || "").toLowerCase() === BODY_FORMAT_HTML} />
      ) : null}

      {attachment ? (
        <DocumentAttachmentPreviewCard
          api={api}
          personId={attachment.personId}
          doc={{
            id: attachment.documentId,
            fileName: attachment.fileName,
            contentType: attachment.contentType || "application/octet-stream",
          }}
          text={text}
        />
      ) : hasAttachment ? (
        <Typography.Text type="secondary">
          {text("Attachment")}: {attachmentFileName || text("Document")}
        </Typography.Text>
      ) : null}

      {!hasBody && !hasAttachment ? (
        <Typography.Text type="secondary">{text("No content.")}</Typography.Text>
      ) : null}
    </div>
  );
}
